<template>
  <div>
    <paper-modal-container :is-visible="true" title="결제 안내" @close="goBack">
      <div class="checkout-wrapper">
        <div v-if="loading" class="state-block">
          <div class="spinner"></div>
          <p class="state-desc">결제 정보를 준비하고 있습니다...</p>
        </div>

        <div v-else-if="errorMessage" class="state-block">
          <div class="error-icon">!</div>
          <p class="state-title state-error">결제 정보를 불러오지 못했습니다</p>
          <p class="state-desc">{{ errorMessage }}</p>
          <button type="button" class="primary-btn" @click="goBack">신청 화면으로 돌아가기</button>
        </div>

        <template v-else>
          <!-- <div v-if="isLocalPreview" class="preview-banner">
            미리보기 모드 — 신청서는 브라우저에만 저장됩니다. 토스 카드 등록 창은 테스트 키로 열 수 있습니다.
          </div> -->

          <!-- 플랜 요약 -->
          <section class="form-section-block">
            <div class="section-header">
              <span class="section-icon">💎</span>
              <h3 class="section-title">선택한 플랜</h3>
            </div>
            <div class="plan-summary">
              <div class="plan-summary-top">
                <span class="plan-summary-name">{{ planSnapshot?.name || "-" }}</span>
                <span class="plan-summary-amount">합계 {{ formatWon(planSnapshot?.totalAmount) }} / 월</span>
              </div>
              <div class="plan-summary-breakdown">
                <span>공급가</span><strong>{{ formatWon(planSnapshot?.monthlyAmount) }}</strong>
                <span>VAT (10%)</span><strong>{{ formatWon(planSnapshot?.vat) }}</strong>
                <span class="total-label">매월 결제 금액</span>
                <strong class="total-amount">{{ formatWon(planSnapshot?.totalAmount) }}</strong>
              </div>
              <p class="plan-summary-impressions">
                {{ planSnapshot?.maxImpressions === null
                  ? "월 노출 한도 없음"
                  : `월 최대 노출 ${(planSnapshot?.maxImpressions || 0).toLocaleString()}회` }}
              </p>
            </div>
          </section>

          <!-- 정기결제 안내 -->
          <section class="form-section-block">
            <div class="section-header">
              <span class="section-icon">🔁</span>
              <h3 class="section-title">정기결제 안내</h3>
            </div>
            <ul class="notice-list">
              <li>등록한 카드로 매월 같은 날 자동 결제됩니다.</li>
              <li>해지는 「내 광고」에서 언제든 가능합니다.</li>
              <li>해지하더라도 이미 결제된 당월 분은 만료일까지 노출이 유지됩니다.</li>
              <li>첫 결제 후 관리자 심사가 진행됩니다 (영업일 기준 1~2일).</li>
              <li>부적합한 소재는 반려될 수 있으며, 반려 시 첫 결제는 환불 처리됩니다.</li>
            </ul>
          </section>

          <!-- 취소·환불 규정 -->
          <section class="form-section-block">
            <div class="section-header">
              <span class="section-icon">↩️</span>
              <h3 class="section-title">취소 및 환불 규정</h3>
            </div>
            <div class="policy-box">
              <div v-for="section in cancelRefundPolicy" :key="section.title" class="policy-section">
                <h4 class="policy-section-title">{{ section.title }}</h4>
                <ul class="policy-list">
                  <li v-for="(item, idx) in section.items" :key="idx">{{ item }}</li>
                </ul>
              </div>
            </div>
          </section>

          <!-- 약관 동의 -->
          <section class="form-section-block">
            <div class="section-header">
              <span class="section-icon">📋</span>
              <h3 class="section-title">약관 동의</h3>
            </div>
            <label class="agreement-row">
              <input type="checkbox" v-model="agreedSubscription" />
              <span>위 정기결제 안내 내용을 모두 확인하였으며 매월 자동 결제에 동의합니다. (필수)</span>
            </label>
            <label class="agreement-row">
              <input type="checkbox" v-model="agreedCancelRefund" />
              <span>위 취소 및 환불 규정을 확인하였으며 이에 동의합니다. (필수)</span>
            </label>
            <label class="agreement-row">
              <input type="checkbox" v-model="agreedTerms" />
              <span>광고 운영 약관 및 개인정보 수집·이용에 동의합니다. (필수)</span>
            </label>
          </section>

          <!-- 결제 버튼 -->
          <div class="action-row">
            <button type="button" class="secondary-btn" :disabled="processing" @click="goBack">이전 단계</button>
            <button type="button" class="primary-btn" :disabled="!canSubmit" @click="onRequestBilling">
              {{ processing ? "결제창 호출 중..." : "카드 등록하고 첫 결제 진행하기" }}
            </button>
          </div>

          <!-- <p v-if="isLocalPreview" class="warn-text preview-hint">
            카드 등록 후 서버 저장·첫 결제 확정은 백엔드 연동 전까지 완료되지 않습니다. 토스 테스트 카드로 등록 흐름만 확인할 수 있습니다.
          </p>
          <p v-else-if="!tossClientKey" class="warn-text">
            ⚠ 토스페이먼츠 클라이언트 키가 설정되지 않았습니다 (VUE_APP_TOSS_CLIENT_KEY).
            관리자에게 문의해주세요.
          </p> -->
        </template>
      </div>
    </paper-modal-container>
  </div>
</template>

<script>
import PaperModalContainer from "@/components/modal/PaperModalContainer.vue";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { useUserStore } from "@/store/user";
import {
  getAdCampaign,
  prepareAdBilling,
  AD_PLAN_MAP,
  formatWon,
  getLocalAdDraft,
  isLocalPreviewCampaignId,
  AD_CANCEL_REFUND_POLICY,
  TOSS_CLIENT_KEY,
  buildAdBillingCustomerKey,
} from "@/utils/adApplication";

const PENDING_KEY_PREFIX = "adBillingPending:";

export default {
  name: "AdCheckout",
  components: { PaperModalContainer },
  setup() {
    const userStore = useUserStore();
    return { userStore };
  },
  data() {
    return {
      campaign: null,
      loading: true,
      errorMessage: "",
      agreedSubscription: false,
      agreedCancelRefund: false,
      agreedTerms: false,
      cancelRefundPolicy: AD_CANCEL_REFUND_POLICY,
      processing: false,
      tossClientKey: TOSS_CLIENT_KEY,
      isLocalPreview: false,
    };
  },
  computed: {
    campaignId() {
      return String(this.$route.params.id || "").trim();
    },
    planSnapshot() {
      if (!this.campaign) return null;
      const snapshot = this.campaign.planSnapshot;
      if (snapshot && Object.keys(snapshot).length) return snapshot;
      return AD_PLAN_MAP[this.campaign.plan] || null;
    },
    canSubmit() {
      return (
        this.agreedSubscription &&
        this.agreedCancelRefund &&
        this.agreedTerms &&
        !this.processing &&
        !!this.planSnapshot?.totalAmount &&
        !!this.tossClientKey
      );
    },
  },
  async mounted() {
    if (!this.campaignId) {
      this.errorMessage = "캠페인 정보가 없습니다.";
      this.loading = false;
      return;
    }
    if (isLocalPreviewCampaignId(this.campaignId)) {
      const local = getLocalAdDraft(this.campaignId);
      if (local) {
        this.campaign = local;
        this.isLocalPreview = true;
        this.loading = false;
        return;
      }
      this.errorMessage = "미리보기 데이터가 없습니다. 신청 화면에서 다시 진행해주세요.";
      this.loading = false;
      return;
    }
    try {
      const campaign = await getAdCampaign(this.campaignId);
      this.campaign = campaign;
      if (!campaign) {
        this.errorMessage = "캠페인을 찾을 수 없습니다.";
      } else if (campaign.status && !["draft", "pending_payment"].includes(campaign.status)) {
        this.errorMessage = "이미 결제가 완료된 광고입니다.";
      }
    } catch (e) {
      console.error("캠페인 로드 실패", e);
      const local = getLocalAdDraft(this.campaignId);
      if (local) {
        this.campaign = local;
        this.isLocalPreview = true;
      } else {
        this.errorMessage = e?.response?.data?.message || e?.message || "캠페인 정보를 불러오지 못했습니다.";
      }
    } finally {
      this.loading = false;
    }
  },
  methods: {
    formatWon,
    goBack() {
      if (this.campaignId) {
        this.$router.push(`/ads/apply/${this.campaignId}`);
      } else {
        this.$router.push("/ads/apply");
      }
    },
    async resolveBillingContext() {
      let useLocalPreview = this.isLocalPreview;

      if (!useLocalPreview) {
        try {
          const prep = await prepareAdBilling(this.campaignId);
          if (prep?.customerKey) {
            return { customerKey: prep.customerKey, useLocalPreview: false };
          }
        } catch (e) {
          console.warn("빌링 준비 API 실패 — 로컬 customerKey로 토스창 연결", e);
          useLocalPreview = true;
        }
      }

      return {
        customerKey: buildAdBillingCustomerKey(this.userStore),
        useLocalPreview,
      };
    },
    saveBillingPending(customerKey, useLocalPreview) {
      sessionStorage.setItem(
        `${PENDING_KEY_PREFIX}${customerKey}`,
        JSON.stringify({
          campaignId: this.campaignId,
          planSnapshot: this.planSnapshot,
          isLocalPreview: useLocalPreview,
          requestedAt: Date.now(),
        })
      );
    },
    async onRequestBilling() {
      if (!this.canSubmit) return;
      if (!this.tossClientKey) {
        this.$alert?.("토스페이먼츠 클라이언트 키가 설정되지 않았습니다.");
        return;
      }
      try {
        this.processing = true;

        const { customerKey, useLocalPreview } = await this.resolveBillingContext();
        if (!customerKey) throw new Error("customerKey를 생성하지 못했습니다.");

        try {
          this.saveBillingPending(customerKey, useLocalPreview);
        } catch (e) {
          console.warn("세션 저장 실패", e);
        }

        const tossPayments = await loadTossPayments(this.tossClientKey);
        const payment = tossPayments.payment({ customerKey });

        const successUrl = `${window.location.origin}/ads/payment/success`;
        const failUrl = `${window.location.origin}/ads/payment/fail`;

        await payment.requestBillingAuth({
          method: "CARD",
          successUrl,
          failUrl,
          customerEmail: this.campaign?.advertiser?.contactEmail || this.userStore.email || undefined,
          customerName: this.campaign?.advertiser?.contactName || this.userStore.nickname || undefined,
        });
        // 성공 시 토스 페이지로 리다이렉트되므로 processing 해제는 fail 콜백에서만 필요
      } catch (e) {
        console.error("빌링 요청 실패", e);
        if (e?.code !== "USER_CANCEL") {
          const msg = e?.message || e?.code || "결제창을 호출하지 못했습니다.";
          this.$alert?.(
            `${msg}\n\n자동결제(빌링) 전용 테스트 키가 필요할 수 있습니다. 토스 개발자센터 > API 키에서 확인해주세요.`
          );
        }
        this.processing = false;
      }
    },
  },
};
</script>

<style scoped>
.checkout-wrapper {
  padding: 24px 0 80px;
  max-width: 720px;
  margin: 0 auto;
  text-align: left;
}

.preview-banner {
  background: #fff3bf;
  color: #5c3c00;
  border: 1px solid #ffe066;
  border-radius: 8px;
  padding: 12px 14px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  margin-bottom: 16px;
  text-align: center;
}

.preview-hint {
  color: #5c3c00;
}

.form-section-block {
  padding: 24px 0;
  border-bottom: 1px solid #e9ecef;
}

.form-section-block:last-of-type {
  border-bottom: none;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.section-icon {
  font-size: 18px;
}

.section-title {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 17px;
  color: #2d3748;
  margin: 0;
}

/* 플랜 요약 */
.plan-summary {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 18px 20px;
  background: #fafbfc;
}

.plan-summary-top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
}

.plan-summary-name {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 18px;
  color: #2d3748;
}

.plan-summary-amount {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 18px;
  color: #1c7ed6;
}

.plan-summary-breakdown {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px 16px;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 13px;
  color: #495057;
}

.plan-summary-breakdown strong {
  font-family: "NanumSquareRoundB", sans-serif;
  color: #212529;
}

.plan-summary-breakdown .total-label {
  border-top: 1px dashed #dee2e6;
  padding-top: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  color: #2d3748;
}

.plan-summary-breakdown .total-amount {
  border-top: 1px dashed #dee2e6;
  padding-top: 8px;
  color: #1c7ed6;
  font-size: 15px;
}

.plan-summary-impressions {
  margin: 12px 0 0;
  font-size: 13px;
  color: #6c757d;
}

/* 안내 리스트 */
.notice-list {
  margin: 0;
  padding-left: 18px;
  color: #495057;
  font-size: 13px;
  line-height: 1.8;
}

/* 취소·환불 규정 */
.policy-box {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 14px 16px;
  background: #fafbfc;
}

.policy-section {
  margin-bottom: 14px;
}

.policy-section:last-child {
  margin-bottom: 0;
}

.policy-section-title {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 13px;
  color: #2d3748;
  margin: 0 0 6px;
}

.policy-list {
  margin: 0;
  padding-left: 18px;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 12px;
  color: #495057;
  line-height: 1.7;
}

.policy-list li {
  margin-bottom: 4px;
}

/* 약관 동의 */
.agreement-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
}

.agreement-row input[type="checkbox"] {
  margin-top: 3px;
  width: 16px;
  height: 16px;
  accent-color: #66bdff;
}

/* 액션 */
.action-row {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 32px;
}

.secondary-btn {
  padding: 12px 22px;
  background: #f1f3f5;
  color: #495057;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
  cursor: pointer;
}

.primary-btn {
  padding: 12px 22px;
  background: #66bdff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 14px;
  cursor: pointer;
}

.primary-btn:hover:enabled {
  background: #4da3ff;
}

.primary-btn:disabled,
.secondary-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.warn-text {
  margin-top: 12px;
  color: #c92a2a;
  font-size: 12px;
  text-align: right;
}

/* 상태 블록 */
.state-block {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #66bdff;
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f56565 0%, #c53030 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-family: "NanumSquareRoundEB", sans-serif;
  margin: 0 auto 20px;
}

.state-title {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 18px;
  color: #2d3748;
  margin: 0 0 8px 0;
}

.state-title.state-error {
  color: #c53030;
}

.state-desc {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 14px;
  color: #4a5568;
  margin: 0 0 8px 0;
  line-height: 1.6;
}

@media (max-width: 480px) {
  .action-row {
    flex-direction: column;
  }

  .secondary-btn,
  .primary-btn {
    width: 100%;
  }
}
</style>
